import { Icon12ArrowUp, Icon20Cancel } from '@vkontakte/icons';
import { Button, Spacing, Textarea } from '@vkontakte/vkui';
import classNames from 'clsx';
import debounce from 'lodash.debounce';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { aiImageGenerationStyles } from 'reactApp/appHelpers/featuresHelpers/features/aiImageGeneration';
import { generateImageStart } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.actions';
import type { AiGenerateImageStyleType } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.types';
import { renderAiGenerationStylesPopup } from 'reactApp/ui/AiPhotoGeneration/AiGenerationStylesPopup/AiGenerationStylesPopup';
import { AiPhotoGenerationSuggestions } from 'reactApp/ui/AiPhotoGeneration/ToolbarIdle/Suggestions/AiPhotoGenerationSuggestions';
import { sendXray } from 'reactApp/utils/ga';

import styles from './AiPhotoGenerationToolbarIdle.css';

const PLACEHOLDER_TEXT = 'Напишите запрос, например: кот смотрит в окно, за окном цветёт вишня';

const getStyleName = (key: string) => aiImageGenerationStyles.find((i) => i.key === key)?.name || key;

// eslint-disable-next-line max-lines-per-function
export const AiPhotoGenerationToolbarIdle = memo(() => {
    const [showSuggestions, setShowSuggestions] = useState<boolean>(true);
    const [showApplyButton, setShowApplyButton] = useState<boolean>(false);
    const [selectedStyle, setSelectedStyle] = useState<AiGenerateImageStyleType | null>(null);

    const [value, setValue] = useState('');

    const dispatch = useDispatch();

    const onPromptChange = useMemo(
        () =>
            debounce((value: string) => {
                if (value.length !== 0) {
                    setShowSuggestions(false);
                    setShowApplyButton(true);
                } else {
                    setShowSuggestions(true);
                    setShowApplyButton(false);
                }
            }, 200),
        []
    );

    useEffect(() => {
        return () => {
            onPromptChange.cancel();
        };
    }, [onPromptChange]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const nextValue = e.target.value;
            setValue(nextValue);
            onPromptChange(nextValue);
        },
        [onPromptChange]
    );

    const onSuggestionClick = useCallback(
        (suggest: string) => {
            setValue(suggest);
            onPromptChange(suggest);
        },
        [onPromptChange]
    );

    const onStylesClick = useCallback(() => {
        renderAiGenerationStylesPopup({ setSelectedStyle });
    }, []);

    const onResetStyleClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedStyle(null);
    }, []);

    const handleGenerationClick = useCallback(() => {
        sendXray(['ai-gen', 'gen', 'click']);

        dispatch(
            generateImageStart(
                selectedStyle
                    ? {
                          prompt: value,
                          style: selectedStyle,
                      }
                    : {
                          prompt: value,
                      }
            )
        );
    }, [dispatch, value, selectedStyle]);

    return (
        <div className={styles.toolbar} data-test-id="ai-photo-generation-toolbar-idle">
            {showSuggestions && <AiPhotoGenerationSuggestions onSuggestionClick={onSuggestionClick} />}

            <div className={styles.wrapper}>
                <Textarea
                    placeholder={PLACEHOLDER_TEXT}
                    value={value}
                    onChange={handleChange}
                    maxHeight={120}
                    data-test-id="prompt-textarea"
                    className={styles.inputContainer}
                    aria-rowcount={6}
                    rows={1}
                />
                <Spacing size={20} />
                <div className={styles.controls}>
                    <Button
                        size="l"
                        data-test-id="button-select-style"
                        onClick={onStylesClick}
                        className={classNames(styles.styleButton, {
                            [styles.styleButtonSelected]: !!selectedStyle,
                        })}
                    >
                        {selectedStyle ? (
                            <div className={styles.selectedButtonWrapper}>
                                {getStyleName(selectedStyle)}
                                <Icon20Cancel className={styles.selectedButtonClose} onClick={onResetStyleClick} />
                            </div>
                        ) : (
                            'Выбрать стиль'
                        )}
                    </Button>

                    {showApplyButton && (
                        <Button
                            size="l"
                            mode="primary"
                            appearance="accent-invariable"
                            before={<Icon12ArrowUp />}
                            data-test-id="button-start-generation"
                            onClick={handleGenerationClick}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

AiPhotoGenerationToolbarIdle.displayName = 'AiPhotoGenerationToolbarIdle';
